/**
 * The stage page's state, as a pure reducer (Lesson Dashboard pass 3, release
 * 5, 2026-09-28). Everything the /stage page shows comes from messages on the
 * lesson's BroadcastChannel; this decides what each message does, so it can
 * be tested without a window, a channel or a clock.
 *
 * Modes:
 *   idle    the logo and "Ready." (nothing shown yet, or the last lesson closed)
 *   lyrics  a track's timed words, scrolling with the lesson's player
 *   video   a saved YouTube video
 *   blank   the logo only; the teacher asked for nothing else on the screen
 *
 * THE VISIBILITY WALL HOLDS BY CONSTRUCTION: the only content a message can
 * carry is a track's words, a validated video id and a title. No note, recap
 * or student data is ever sent, and nothing here would render it if it were.
 */

export const STAGE_MODES = ['idle', 'lyrics', 'video', 'blank'];
const VIDEO_ID = /^[A-Za-z0-9_-]{6,20}$/;

export const initialStageState = { mode: 'idle', video: null, lyrics: null, clock: null };

/** @param nowMs the moment the message arrived, so the clock can run on. */
export const stageReducer = (state = initialStageState, msg = {}, nowMs = 0) => {
	if (!msg || typeof msg !== 'object') return state;
	switch (msg.type) {
		case 'lyrics': {
			// Words ALWAYS load, so "Lyrics" works whenever a track with timed
			// words is in the lesson; they only TAKE THE SCREEN when the message
			// says `show` (a new track starting to play). Before 2026-09-28 every
			// lyrics message took the screen, and the dashboard re-sends them on
			// ordinary re-renders, so a teacher's Blank or Video snapped back to
			// Lyrics seconds later: "the buttons ... don't seem to work".
			const lines = Array.isArray(msg.lines) ? msg.lines : [];
			if (!msg.key || !lines.length) return state;
			const next = lines.map((l) => ({ t: Number(l.t) || 0, text: String(l.text || '') }));
			// The SAME WORDS for the same track are a re-send (the dashboard
			// re-renders often): keep the object, so nothing about the scroll
			// resets. DIFFERENT words for the same track are new information, a
			// track timed after it started playing or words corrected mid-lesson
			// (2026-10-05, Red and Maddy): take them, in the current mode.
			const sameKey = Boolean(state.lyrics && state.lyrics.key === msg.key);
			const sameWords =
				sameKey &&
				state.lyrics.lines.length === next.length &&
				state.lyrics.lines.every((l, i) => l.t === next[i].t && l.text === next[i].text);
			return {
				...state,
				mode: msg.show ? 'lyrics' : state.mode,
				lyrics: sameWords
					? state.lyrics
					: {
							key: msg.key,
							title: String(msg.title || ''),
							lines: next,
							unverified: Boolean(msg.unverified),
					  },
				clock: { key: msg.key, t: Number(msg.t) || 0, playing: Boolean(msg.playing), at: nowMs },
			};
		}
		case 'clock':
			if (!state.lyrics || msg.key !== state.lyrics.key) return state;
			return { ...state, clock: { key: msg.key, t: Number(msg.t) || 0, playing: Boolean(msg.playing), at: nowMs } };
		case 'video': {
			const id = String(msg.videoId || '');
			if (!VIDEO_ID.test(id)) return state;
			return { ...state, mode: 'video', video: { videoId: id, title: String(msg.title || 'Video') } };
		}
		case 'mode': {
			// A requested mode only takes when there is something to show in it:
			// "Lyrics" with no words, or "Video" with no video, stays put.
			const m = msg.mode;
			if (!STAGE_MODES.includes(m)) return state;
			if (m === 'lyrics' && !state.lyrics) return state;
			if (m === 'video' && !state.video) return state;
			return { ...state, mode: m };
		}
		case 'reset':
			// The lesson closed: back to the logo, and forget its words and
			// video so nothing of it can be brought back by Lyrics or Video.
			return initialStageState;
		default:
			return state;
	}
};

/** Where the lyrics clock is now, in seconds, plus the lead. */
export const clockNow = (clock, nowMs, lead = 0) => {
	if (!clock) return 0;
	return (Number(clock.t) || 0) + (clock.playing ? (nowMs - (clock.at || 0)) / 1000 : 0) + lead;
};

/** The index of the line being sung at `t` (-1 before the first). */
export const lineIndexAt = (lines = [], t = 0) => {
	let i = -1;
	for (let k = 0; k < lines.length; k += 1) {
		if (Number(lines[k].t) <= t) i = k;
		else break;
	}
	return i;
};

/** What the stage could show, for its heartbeat: the lesson's menu reads it. */
export const stageHas = (state) => ({ lyrics: Boolean(state && state.lyrics), video: Boolean(state && state.video) });

/** The words for the dashboard's status line: "Lyrics", "Video", "Blank", "Ready". */
export const stageModeLabel = (mode) => ({ lyrics: 'Lyrics', video: 'Video', blank: 'Blank', idle: 'Ready' }[mode] || 'Ready');
